import { useState } from 'react';

import { Badge, Button, DataTable, type DataTableColumn } from '~/components/ui';
import { LocalTime } from '~/components/common/LocalTime';
import type { MlsRow, MlsTarget } from '~/components/Constants/mlsConstants';
import { routes } from '~/app/routes';
import { feedCustomers } from '~/components/pages/mls/components/viewCustomersTable';
import { feedId } from '~/components/pages/mls/components/feedActions';
import MlsQuickActions from '~/components/pages/mls/components/mlsQuickActions';

const Unset = () => <span className="text-gray-400 dark:text-gray-500">—</span>;

// Color carries the state; the word stays for screen readers and in the tooltip with the feed key.
export const BayTarget = ({ label, target }: { label: string; target?: MlsTarget }) =>
  target?.key ? (
    <Badge color="success" size="sm" title={`${label} connected — feed ${target.key}`}>
      {label}
      <span className="sr-only"> connected</span>
    </Badge>
  ) : (
    <Badge color="error" size="sm" title={`${label} not connected`}>
      {label}
      <span className="sr-only"> not connected</span>
    </Badge>
  );

// Feeds' iconUrl is a derived S3/CDN URL that 404s until a logo has been uploaded —
// fall back to a visible placeholder rather than a broken image icon.
export const MlsLogo = ({ src, name }: { src?: string; name?: string }) => {
  // Remember WHICH src failed, not merely that one did. React keeps this component
  // mounted across a table refresh, so a plain boolean would leave the placeholder in
  // place after a successful upload changed the src — the very thing the upload is
  // meant to show. Comparing against the current src resets it for free.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const failed = failedSrc === src;

  if (!src || failed) {
    return (
      <span
        className="flex size-8 items-center justify-center text-gray-400 dark:text-gray-500"
        role="img"
        aria-label={`${name} logo unavailable`}
      >
        —
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`${name} logo`}
      className="size-8 object-contain"
      loading="lazy"
      onError={() => setFailedSrc(src)}
    />
  );
};

interface MLSTableProps {
  data?: MlsRow[];
  listLoad: boolean;
  /** A search or filter is narrowing the list, so an empty table means "no match". */
  /** A failed list load, shown in place of the rows. */
  listError?: { what: string; message?: string; onRetry?: () => void };
  filtered: boolean;
  onDelete: (row: MlsRow) => void;
}

const MLSTable = ({ data, listLoad, listError, filtered, onDelete }: MLSTableProps) => {
  const rows = data ?? [];

  const columns: DataTableColumn<MlsRow>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <>
          <span className="font-medium text-gray-800 dark:text-white/90">
            {row.name ?? row._id}
          </span>
          <span className="block font-mono text-xs text-gray-500 dark:text-gray-400">
            {row._id}
          </span>
        </>
      ),
    },
    { id: 'iconUrl', header: 'Logo', cell: (row) => <MlsLogo src={row.iconUrl} name={row.name} /> },
    {
      id: 'platforms',
      header: 'Platforms',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <BayTarget label="Office" target={row.targets?.ClientBAY} />
          <BayTarget label="Social" target={row.targets?.SocialBAY} />
        </div>
      ),
    },
    {
      id: 'dtLastDownload',
      header: 'Last download',
      cell: (row) => <LocalTime value={row.dtLastDownload} />,
    },
    {
      id: 'dtLastDownloadFull',
      header: 'Last full download',
      cell: (row) => <LocalTime value={row.dtLastDownloadFull} />,
    },
    {
      id: 'companyIds',
      header: 'Customers',
      cell: (row) => (
        <Button variant="link" href={routes.feed(feedId(row))}>
          {feedCustomers(row).length}
        </Button>
      ),
    },
    { id: 'dataServer', header: 'Data server', cell: (row) => row.dataServer || <Unset /> },
    { id: 'dmServer', header: 'DM server', cell: (row) => row.dmServer || <Unset /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (row) => <MlsQuickActions rowData={row} onDelete={onDelete} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={feedId}
      href={(row) => routes.feed(feedId(row))}
      rowLabel={(row) => row.name ?? feedId(row)}
      error={listError}
      loading={listLoad && !rows.length}
      refreshing={listLoad && rows.length > 0}
      emptyMessage={filtered ? 'No feeds match.' : 'No feeds yet.'}
      emptyDescription={
        filtered
          ? 'Clear the search or filters to see more feeds.'
          : 'Feeds appear here once they are configured.'
      }
      testId="feeds-table"
    />
  );
};

export default MLSTable;
